import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { minutesToHHMM, hhmmToMinutes, snapMinutes } from "./timeUtils";
import { X, Dumbbell, Trophy, Activity, Zap, Heart, Brain, Clock, Moon, Trash2, Undo2, Redo2, Info, Save, Plus, Lock, AlertCircle, FileText, Edit3, Users, User, Link, Calendar, Download } from "lucide-react";

const MAX_HISTORY = 50;

function getSessionStyle(label) {
  const l = String(label || "").toLowerCase();
  
  if (/\brest\b|jour\s*off|day\s*off|congé|repos\s*complet/i.test(l)) {
    return {
      icon: Moon,
      bgLight: "bg-slate-400",
      bgDark: "bg-slate-500",
      borderLight: "border-slate-300",
      borderDark: "border-slate-400",
      textClass: "text-white",
      accentClass: "bg-slate-300/30",
    };
  }
  
  if (/match|game|compétition|competition/i.test(l)) {
    return {
      icon: Trophy,
      bgLight: "bg-amber-500",
      bgDark: "bg-amber-600",
      borderLight: "border-amber-400",
      borderDark: "border-amber-500",
      textClass: "text-white",
      accentClass: "bg-amber-400/30",
    };
  }
  if (/récup|recup|recovery|repos/i.test(l)) {
    return {
      icon: Heart,
      bgLight: "bg-emerald-500",
      bgDark: "bg-emerald-600",
      borderLight: "border-emerald-400",
      borderDark: "border-emerald-500",
      textClass: "text-white",
      accentClass: "bg-emerald-400/30",
    };
  }
  if (/force|strength|muscul|gym/i.test(l)) {
    return {
      icon: Dumbbell,
      bgLight: "bg-rose-500",
      bgDark: "bg-rose-600",
      borderLight: "border-rose-400",
      borderDark: "border-rose-500",
      textClass: "text-white",
      accentClass: "bg-rose-400/30",
    };
  }
  if (/tactique|tactic|video|analyse/i.test(l)) {
    return {
      icon: Brain,
      bgLight: "bg-violet-500",
      bgDark: "bg-violet-600",
      borderLight: "border-violet-400",
      borderDark: "border-violet-500",
      textClass: "text-white",
      accentClass: "bg-violet-400/30",
    };
  }
  if (/intensif|intense|hiit|sprint/i.test(l)) {
    return {
      icon: Zap,
      bgLight: "bg-orange-500",
      bgDark: "bg-orange-600",
      borderLight: "border-orange-400",
      borderDark: "border-orange-500",
      textClass: "text-white",
      accentClass: "bg-orange-400/30",
    };
  }
  return {
    icon: Activity,
    bgLight: "bg-sky-500",
    bgDark: "bg-sky-600",
    borderLight: "border-sky-400",
    borderDark: "border-sky-500",
    textClass: "text-white",
    accentClass: "bg-sky-400/30",
  };
}

const START_MIN = 6 * 60;
const END_MIN = 22 * 60;
const GRID_STEP = 15;
const MIN_DUR = 15;
const BASE_PX_PER_MIN = 2.0;
const ZOOM_MIN_ABS = 0.1;
const ZOOM_MAX = 2.0;

function clamp(v, a, b) {
  return Math.max(a, Math.min(b, v));
}


function computeLanesForDay(items) {
  const isPlayerSpecificSession = (it) => it.is_player_specific === true || !!(it.player_id || it.player_ids?.length > 0);
  
  const generalSessions = items.filter(it => !isPlayerSpecificSession(it));
  const playerSessions = items.filter(it => isPlayerSpecificSession(it));
  
  const sorted = [...items].sort((a, b) => (a.start_min - b.start_min) || (a.duration_min - b.duration_min));
  
  const placed = [];
  const hasOverlappingPlayerSessions = new Map();

  for (const it of generalSessions) {
    placed.push({ uid: it.uid, laneIndex: 0 });
    hasOverlappingPlayerSessions.set(it.uid, false);
  }

  for (const playerSession of playerSessions) {
    const pStart = playerSession.start_min;
    const pEnd = pStart + playerSession.duration_min;
    
    const overlappingGeneral = generalSessions.filter(gs => {
      const gStart = gs.start_min;
      const gEnd = gStart + gs.duration_min;
      return !(gEnd <= pStart || pEnd <= gStart);
    });
    
    for (const gs of overlappingGeneral) {
      hasOverlappingPlayerSessions.set(gs.uid, true);
    }
    
    let laneIndex = 1;
    
    const usedLanes = new Set();
    for (const otherPlaced of placed) {
      const other = items.find(i => i.uid === otherPlaced.uid);
      if (!other) continue;
      if (!isPlayerSpecificSession(other)) continue;
      
      const oStart = other.start_min;
      const oEnd = oStart + other.duration_min;
      const overlap = !(oEnd <= pStart || pEnd <= oStart);
      if (overlap) {
        usedLanes.add(otherPlaced.laneIndex);
      }
    }
    
    while (usedLanes.has(laneIndex)) {
      laneIndex++;
    }
    
    placed.push({ uid: playerSession.uid, laneIndex });
  }

  const laneIndexByUid = new Map(placed.map((p) => [p.uid, p.laneIndex]));
  const laneCountByUid = new Map();

  for (const it of sorted) {
    const a0 = it.start_min;
    const a1 = it.start_min + it.duration_min;
    let maxLane = 0;
    for (const other of sorted) {
      const b0 = other.start_min;
      const b1 = other.start_min + other.duration_min;
      const overlap = !(b1 <= a0 || a1 <= b0);
      if (!overlap) continue;
      const li = laneIndexByUid.get(other.uid) ?? 0;
      maxLane = Math.max(maxLane, li);
    }
    laneCountByUid.set(it.uid, maxLane + 1);
  }

  return { laneIndexByUid, laneCountByUid, hasOverlappingPlayerSessions };
}

function checkCollisionWithGeneralSessions(sessions, movingUid, dayIndex, newStartMin, newDurationMin) {
  const newEndMin = newStartMin + newDurationMin;
  
  const isPlayerSpecificSession = (s) => s.is_player_specific === true || !!(s.player_id || s.player_ids?.length > 0);
  
  for (const s of sessions) {
    if (s.uid === movingUid) continue;
    if (s.day_index !== dayIndex) continue;
    if (isPlayerSpecificSession(s)) continue;
    if (s.event_type === "match") continue;
    
    const sStart = s.start_min;
    const sEnd = sStart + s.duration_min;
    
    const overlaps = !(sEnd <= newStartMin || newEndMin <= sStart);
    if (overlaps) {
      return s;
    }
  }
  
  return null;
}

export default function MicrocycleCalendar({
  isDarkMode,
  dayLabels,
  dayMeta,
  daysCount,
  sessions,
  onChangeSessions,
  onDropFromLibrary,
  sessionTemplates,
  onDoubleClickSession,
  onCreateNewSession,
  onUpdateSessionDetails,
  onEditSession,
  onRemoveSession,
  onSessionMoved,
  calendarEventsAvailable = [],
  onImportCalendarEvents,
  readOnly = false,
}) {
  const shell = isDarkMode ? "bg-gray-900 border-gray-700" : "bg-white border-gray-200";
  const muted = isDarkMode ? "text-gray-400" : "text-gray-600";
  const gridLine = isDarkMode ? "rgba(255,255,255,0.06)" : "rgba(17,24,39,0.08)";

  const wrapRef = useRef(null);
  const [drag, setDrag] = useState(null);
  // drag = { kind: "move"|"new", uid, startY, startX, dayIndex, startStart, startDur, newTemplate }
  const [hoverDay, setHoverDay] = useState(null);
  const [zoomMinFit, setZoomMinFit] = useState(ZOOM_MIN_ABS);
  const [zoom, setZoom] = useState(0.1);
  const [wrapSize, setWrapSize] = useState({ w: 0, h: 0 });
  
  // Current time indicator state
  const [currentTimeMin, setCurrentTimeMin] = useState(() => {
    const now = new Date();
    return now.getHours() * 60 + now.getMinutes();
  });
  
  // Session edit modal state
  const [editSessionModal, setEditSessionModal] = useState(null); // { uid, label, description }

  // Context menu state
  const [contextMenu, setContextMenu] = useState(null); // { x, y, uid, label, start_min, duration_min }
  // Empty space context menu state
  const [emptyContextMenu, setEmptyContextMenu] = useState(null); // { x, y, dayIndex, start_min }
  // Delete confirmation modal state
  const [deleteConfirmModal, setDeleteConfirmModal] = useState(null); // { uid, label }

  // Undo/Redo history - using a single ref to avoid state sync issues
  const historyRef = useRef({
    stack: [],
    index: -1,
    initialized: false,
    initialLoadComplete: false,
  });
  const [historyVersion, setHistoryVersion] = useState(0); // Force re-render when history changes
  const isUndoRedoAction = useRef(false);
  const isDraggingRef = useRef(false); // Track dragging to skip intermediate states
  const lastSessionsJsonRef = useRef("");
  const initialLoadTimerRef = useRef(null);

  const minutesRange = END_MIN - START_MIN;
  const headerH = 44;
  const timeColW = 64;
  const dayColMinW = 160;
  const WRAP_H = 640;

  const pxPerMin = useMemo(() => BASE_PX_PER_MIN * zoom, [zoom]);
  const heightPx = minutesRange * pxPerMin;

  const availW = Math.max(0, (wrapSize.w || 0) - timeColW);
  const fillDayW = daysCount > 0 ? availW / daysCount : dayColMinW;

  // Ensure no empty gap at min zoom: columns stretch to fill the window.
  // Horizontal scroll appears only once zoom makes columns wider than the window.
  const dayColW = useMemo(
    () => Math.max(dayColMinW * zoom, fillDayW),
    [zoom, dayColMinW, fillDayW]
  );

  // Track sessions changes for undo/redo
  useEffect(() => {
    // Skip if this is an undo/redo action
    if (isUndoRedoAction.current) {
      isUndoRedoAction.current = false;
      return;
    }
    // Skip recording while dragging (we only want start and end states)
    if (isDraggingRef.current) {
      return;
    }
    
    // Don't record if sessions is null/undefined
    if (!sessions) return;
    
    const sessionsArray = [...(sessions || [])];
    const sessionsJson = JSON.stringify(sessionsArray);
    
    // Skip if sessions haven't actually changed
    if (sessionsJson === lastSessionsJsonRef.current) return;
    
    const h = historyRef.current;
    
    // During initial load, we wait for the data to stabilize before recording
    // This prevents recording intermediate states (empty -> partial -> full)
    if (!h.initialLoadComplete) {
      // Clear any pending timer
      if (initialLoadTimerRef.current) {
        clearTimeout(initialLoadTimerRef.current);
      }
      
      // Wait for sessions to stabilize (no changes for 500ms)
      initialLoadTimerRef.current = setTimeout(() => {
        const h = historyRef.current;
        const currentSessions = [...(sessions || [])];
        const currentJson = JSON.stringify(currentSessions);
        
        h.initialLoadComplete = true;
        h.initialized = true;
        h.stack = [currentSessions];
        h.index = 0;
        lastSessionsJsonRef.current = currentJson;
        setHistoryVersion((v) => v + 1);
      }, 500);
      
      return;
    }
    
    lastSessionsJsonRef.current = sessionsJson;
    
    // Truncate any future history (in case we had done undo and then made a new change)
    h.stack = h.stack.slice(0, h.index + 1);
    
    // Add new state
    h.stack.push(sessionsArray);
    
    // Trim if exceeds max
    if (h.stack.length > MAX_HISTORY) {
      h.stack.shift();
    } else {
      h.index = h.stack.length - 1;
    }
    
    setHistoryVersion((v) => v + 1);
  }, [sessions]);
  
  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (initialLoadTimerRef.current) {
        clearTimeout(initialLoadTimerRef.current);
      }
    };
  }, []);

  // Update current time every minute
  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      setCurrentTimeMin(now.getHours() * 60 + now.getMinutes());
    };
    
    // Update immediately
    updateTime();
    
    // Calculate ms until next minute to sync with clock
    const now = new Date();
    const msUntilNextMinute = (60 - now.getSeconds()) * 1000 - now.getMilliseconds();
    
    // First timeout to sync with minute boundary
    const syncTimeout = setTimeout(() => {
      updateTime();
      // Then update every minute
      const interval = setInterval(updateTime, 60000);
      return () => clearInterval(interval);
    }, msUntilNextMinute);
    
    return () => clearTimeout(syncTimeout);
  }, []);

  // Calculate current time indicator position
  const currentTimeY = useMemo(() => {
    if (currentTimeMin < START_MIN || currentTimeMin > END_MIN) return null;
    return (currentTimeMin - START_MIN) * pxPerMin;
  }, [currentTimeMin, pxPerMin]);

  const canUndo = historyRef.current.index > 0;
  const canRedo = historyRef.current.index < historyRef.current.stack.length - 1;

  const handleUndo = useCallback(() => {
    const h = historyRef.current;
    if (h.index <= 0) return;
    
    const prevIndex = h.index - 1;
    const prevState = h.stack[prevIndex];
    if (!prevState || !Array.isArray(prevState)) return;
    
    isUndoRedoAction.current = true;
    h.index = prevIndex;
    lastSessionsJsonRef.current = JSON.stringify(prevState);
    setHistoryVersion((v) => v + 1);
    onChangeSessions(prevState.map(s => ({ ...s })));
  }, [onChangeSessions]);

  const handleRedo = useCallback(() => {
    const h = historyRef.current;
    if (h.index >= h.stack.length - 1) return;
    
    const nextIndex = h.index + 1;
    const nextState = h.stack[nextIndex];
    if (!nextState || !Array.isArray(nextState)) return;
    
    isUndoRedoAction.current = true;
    h.index = nextIndex;
    lastSessionsJsonRef.current = JSON.stringify(nextState);
    setHistoryVersion((v) => v + 1);
    onChangeSessions(nextState.map(s => ({ ...s })));
  }, [onChangeSessions]);

  // Save state and handler
  const [saveMessage, setSaveMessage] = useState(null); // null | "saving" | "saved"
  const saveTimeoutRef = useRef(null);

  const handleSave = useCallback(() => {
    // Clear any existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    setSaveMessage("saving");
    
    // TODO: Implement actual save logic here
    // For now, simulate a save with a small delay
    setTimeout(() => {
      setSaveMessage("saved");
      
      // Clear the message after 2 seconds
      saveTimeoutRef.current = setTimeout(() => {
        setSaveMessage(null);
      }, 2000);
    }, 300);
  }, []);

  // Cleanup save timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  // Keyboard shortcuts for undo/redo
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ctrl+Z or Cmd+Z (key can be 'z' or 'Z' depending on browser)
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "z" && !e.shiftKey) {
        e.preventDefault();
        handleUndo();
      }
      // Ctrl+Shift+Z or Cmd+Shift+Z (key can be 'z' or 'Z' depending on browser)
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "z" && e.shiftKey) {
        e.preventDefault();
        handleRedo();
      }
      // Ctrl+S or Cmd+S to save
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();
        handleSave();
      }
      // Escape to close context menu
      if (e.key === "Escape") {
        setContextMenu(null);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleUndo, handleRedo, handleSave]);

  // Prevent browser zoom when Ctrl+wheel on calendar (needs native listener with passive: false)
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;

    const handleWheel = (e) => {
      if (e.ctrlKey) {
        e.preventDefault();
        e.stopPropagation();
        
        const delta = e.deltaY > 0 ? -0.1 : 0.1;
        const nextZoom = clamp(zoom + delta, zoomMinFit, ZOOM_MAX);
        
        // Get mouse position relative to the scroll container
        const rect = el.getBoundingClientRect();
        const mouseX = e.clientX - rect.left;
        const mouseY = e.clientY - rect.top;
        
        // Current scroll position
        const scrollLeft = el.scrollLeft;
        const scrollTop = el.scrollTop;
        
        // Position in content coordinates
        const contentX = scrollLeft + mouseX;
        const contentY = scrollTop + mouseY;
        
        // Scale factor
        const scale = nextZoom / zoom;
        
        // New content position after zoom
        const newContentX = contentX * scale;
        const newContentY = contentY * scale;
        
        // New scroll position to keep mouse position fixed
        const newScrollLeft = newContentX - mouseX;
        const newScrollTop = newContentY - mouseY;
        
        setZoom(nextZoom);
        
        // Apply new scroll position after state update
        requestAnimationFrame(() => {
          if (el) {
            el.scrollLeft = Math.max(0, newScrollLeft);
            el.scrollTop = Math.max(0, newScrollTop);
          }
        });
      }
    };

    // Must use passive: false to be able to preventDefault
    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleWheel);
  }, [zoom, zoomMinFit]);

  // Close context menu on click outside
  useEffect(() => {
    const handleClick = () => {
      setContextMenu(null);
      setEmptyContextMenu(null);
    };
    if (contextMenu || emptyContextMenu) {
      window.addEventListener("click", handleClick);
      return () => window.removeEventListener("click", handleClick);
    }
  }, [contextMenu, emptyContextMenu]);

  // Fit-to-view minimum zoom: at min zoom, the whole calendar should be visible without scroll.
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;

    const recompute = () => {
      const w = el.clientWidth || 0;
      const h = el.clientHeight || 0;
      setWrapSize({ w, h });
      const availW = Math.max(0, w - timeColW);
      const availH = Math.max(0, h - headerH);

      const fitW = daysCount > 0 ? availW / (daysCount * dayColMinW) : 1;
      const fitH = availH > 0 ? availH / (minutesRange * BASE_PX_PER_MIN) : 1;

      const nextMin = clamp(Math.min(fitW, fitH), ZOOM_MIN_ABS, ZOOM_MAX);
      setZoomMinFit(nextMin);
      setZoom((z) => (z < nextMin ? nextMin : z));
    };

    recompute();
    const ro = new ResizeObserver(recompute);
    ro.observe(el);
    return () => ro.disconnect();
  }, [daysCount, minutesRange]);

  const ticks = useMemo(() => {
    const arr = [];
    for (let m = START_MIN; m <= END_MIN; m += 30) arr.push(m);
    return arr;
  }, []);

  // lane layout per day
  const laneMaps = useMemo(() => {
    const perDay = Array.from({ length: daysCount }).map(() => []);
    for (const s of sessions || []) {
      const d = clamp(s.day_index ?? 0, 0, daysCount - 1);
      perDay[d].push(s);
    }
    return perDay.map((items) => computeLanesForDay(items));
  }, [sessions, daysCount]);

  const dayColumns = useMemo(() => {
    return Array.from({ length: daysCount }).map((_, i) => {
      const meta = dayMeta?.[i];
      return {
        index: i,
        md: meta?.md ?? (dayLabels?.[i] ?? `J${i + 1}`),
        date: meta?.date ?? "",
      };
    });
  }, [daysCount, dayLabels, dayMeta]);

  // Column named exactly "MD" (not MD-6 / MD+1 / etc)
  const mdExactIndex = useMemo(() => {
    const idx = dayColumns.findIndex(
      (d) => String(d.md || "").trim().toUpperCase() === "MD"
    );
    return idx >= 0 ? idx : -1;
  }, [dayColumns]);

  // Objective column = the day which contains a Match session (first match found)
  const objectiveDayIndex = useMemo(() => {
    const match = (sessions || []).find((s) => /\bmatch\b/i.test(String(s.label || "")));
    if (!match) return -1;
    return clamp(match.day_index ?? 0, 0, daysCount - 1);
  }, [sessions, daysCount]);

  const hasObjective = objectiveDayIndex >= 0;

  // Helper: get day index from pointer x
  const getDayIndexFromX = (clientX) => {
    const el = wrapRef.current;
    if (!el) return 0;
    const rect = el.getBoundingClientRect();
    const x = clientX - rect.left - timeColW;
    const dayW = Math.max(1, dayColW);
    const idx = Math.floor(x / dayW);
    return clamp(idx, 0, daysCount - 1);
  };

  // Helper: compute minutes from pointer y
  const getMinuteFromY = (clientY) => {
    const el = wrapRef.current;
    if (!el) return START_MIN;
    const rect = el.getBoundingClientRect();
    const y = clientY - rect.top - headerH;
    const m = START_MIN + y / pxPerMin;
    return clamp(snapMinutes(m, GRID_STEP), START_MIN, END_MIN - MIN_DUR);
  };

  // pointer handlers
  useEffect(() => {
    const onMove = (e) => {
      if (!drag) return;
      const dayIndex = getDayIndexFromX(e.clientX);
      setHoverDay(dayIndex);

      if (drag.kind === "new") {
        // show hover day only
        return;
      }

      const dy = (e.clientY - drag.startY) / pxPerMin;
      const deltaMin = snapMinutes(dy, GRID_STEP);

      if (drag.kind === "move") {
        const nextStart = clamp(
          snapMinutes(drag.startStart + deltaMin, GRID_STEP),
          START_MIN,
          END_MIN - Math.max(MIN_DUR, drag.startDur)
        );
        const nextDay = dayIndex;
        
        // Get the session being moved
        const movingSession = (sessions || []).find(s => s.uid === drag.uid);
        const isPlayerSpecific = movingSession?.player_id || movingSession?.player_ids?.length > 0;
        
        // Only check collisions for general sessions (not player-specific ones)
        if (!isPlayerSpecific) {
          // Check for collision with other general sessions
          const collision = checkCollisionWithGeneralSessions(
            sessions || [],
            drag.uid,
            nextDay,
            nextStart,
            drag.startDur
          );
          
          if (collision) {
            // Collision detected - prevent this move
            // Keep the session in its current valid position
            return;
          }
        }

        onChangeSessions(
          (sessions || []).map((s) =>
            s.uid === drag.uid
              ? { ...s, day_index: nextDay, start_min: nextStart }
              : s
          )
        );
      }

      // Resize is disabled — session duration is driven by blocks
    };

    const onUp = (e) => {
      if (!drag) return;

      if (drag.kind === "new") {
        const dayIndex = getDayIndexFromX(e.clientX);
        const startMin = getMinuteFromY(e.clientY);

        onDropFromLibrary(drag.newTemplate, dayIndex, startMin, drag.newTemplate.total_duration_min || 90);
      }

      // End drag - set this BEFORE forcing a re-record
      isDraggingRef.current = false;
      
      // For move, force the useEffect to run and record the final state
      // by triggering onChangeSessions after isDraggingRef is false
      // This ensures the drag result is recorded in history for undo/redo
      if (drag.kind === "move") {
        const updatedSessions = (sessions || []).map(s => ({...s}));
        onChangeSessions(updatedSessions);
        
        // Find the moved/resized session and notify for calendar sync
        const movedSession = updatedSessions.find(s => s.uid === drag.uid);
        if (movedSession && onSessionMoved) {
          // Get the new day's date from dayMeta
          const newDayDate = dayMeta?.[movedSession.day_index]?.date;
          const newPeriod = movedSession.start_min < 12 * 60 ? "morning" : "afternoon";
          
          onSessionMoved({
            ...movedSession,
            // Update calendar info with new position
            new_calendar_date: newDayDate,
            new_calendar_period: newPeriod,
            // Old position (for deletion if day changed)
            old_calendar_date: drag.dayIndex !== movedSession.day_index 
              ? dayMeta?.[drag.dayIndex]?.date 
              : null,
            old_calendar_period: drag.dayIndex !== movedSession.day_index 
              ? (drag.startStart < 12 * 60 ? "morning" : "afternoon")
              : null,
          });
        }
      }
      
      setDrag(null);
      setHoverDay(null);
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
  }, [drag, sessions, daysCount, onChangeSessions, onDropFromLibrary, onSessionMoved, dayMeta]);

  const startMove = (uid, e) => {
    if (readOnly) return;
    e.preventDefault();
    e.stopPropagation();
    const s = (sessions || []).find((x) => x.uid === uid);
    if (!s) return;
    // Block match modification
    if (s.event_type === "match") return;
    // Block player-specific session modification - they follow the global session
    const isPlayerSpecific = s.is_player_specific === true || !!(s.player_id || s.player_ids?.length > 0);
    if (isPlayerSpecific) return;
    isDraggingRef.current = true; // Start tracking drag
    setDrag({
      kind: "move",
      uid,
      startY: e.clientY,
      startX: e.clientX,
      pxPerMinAtStart: pxPerMin,
      dayIndex: s.day_index,
      startStart: s.start_min,
      startDur: s.duration_min,
    });
  };

  // Resize is disabled — session duration is driven by blocks in the editor

  // Update session details (label/description) and sync with global calendar
  const updateSessionDetails = useCallback((uidToEdit, updates) => {
    if (readOnly) return;
    const session = (sessions || []).find((s) => s.uid === uidToEdit);
    if (!session) return;
    // Block match modification
    if (session.event_type === "match") return;
    
    const updatedSessions = (sessions || []).map((s) => 
      s.uid === uidToEdit ? { ...s, ...updates } : s
    );
    onChangeSessions(updatedSessions);
    
    // Sync with global calendar if callback provided and session has calendar link
    if (onUpdateSessionDetails && session.calendar_event_id) {
      onUpdateSessionDetails({
        event_id: session.calendar_event_id,
        date: session.calendar_date,
        period: session.calendar_period,
        type: "training",
        label: updates.label ?? session.label,
        description: updates.description ?? session.description,
      });
    }
  }, [sessions, onChangeSessions, onUpdateSessionDetails]);

  // Open session edit modal
  const openEditSessionModal = (session) => {
    if (readOnly) return;
    if (session.event_type === "match") return;
    setEditSessionModal({
      uid: session.uid,
      label: session.label || "",
      description: session.description || "",
      event_type: session.event_type,
    });
  };

  // Save session edit modal
  const saveEditSessionModal = () => {
    if (!editSessionModal) return;
    updateSessionDetails(editSessionModal.uid, {
      label: editSessionModal.label,
      description: editSessionModal.description,
    });
    setEditSessionModal(null);
  };

  // Drag from library (HTML5 DnD)
  const onDragOver = (e) => {
    if (readOnly) return;
    e.preventDefault();
    const dayIndex = getDayIndexFromX(e.clientX);
    setHoverDay(dayIndex);
  };

  const onDrop = (e) => {
    if (readOnly) return;
    e.preventDefault();
    const raw = e.dataTransfer.getData("application/x-session-template");
    if (!raw) return;
    let tpl = null;
    try {
      tpl = JSON.parse(raw);
    } catch {
      tpl = null;
    }
    if (!tpl?.id) return;

    const dayIndex = getDayIndexFromX(e.clientX);
    const startMin = getMinuteFromY(e.clientY);
    onDropFromLibrary(tpl, dayIndex, startMin, tpl.total_duration_min || 90);
    setHoverDay(null);
  };

  const removeSession = (uidToRemove) => {
    if (readOnly) return;
    const session = (sessions || []).find((s) => s.uid === uidToRemove);
    // Block match deletion
    if (session?.event_type === "match") return;
    
    // Note: We no longer sync with global calendar - microcycle is independent
    // Just remove from local state
    onChangeSessions((sessions || []).filter((s) => s.uid !== uidToRemove));
  };

  const inlineEditTime = (uidToEdit, nextStartStr, nextDurStr) => {
    const nextStart = clamp(hhmmToMinutes(nextStartStr), START_MIN, END_MIN - MIN_DUR);
    const nextDur = clamp(parseInt(nextDurStr || "0", 10) || MIN_DUR, MIN_DUR, END_MIN - nextStart);
    onChangeSessions(
      (sessions || []).map((s) => (s.uid === uidToEdit ? { ...s, start_min: snapMinutes(nextStart, GRID_STEP), duration_min: snapMinutes(nextDur, GRID_STEP) } : s))
    );
  };

  return (
    <div className={`rounded-2xl border ${shell} overflow-hidden ${readOnly ? 'ring-2 ring-blue-500/30' : ''}`}>
      <div className="px-4 py-3">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-sm font-semibold">Microcycle calendar</p>
            {readOnly ? (
              <p className="text-xs text-blue-400 flex items-center gap-1">
                <span>🔒</span> Calendar locked — simulation running
              </p>
            ) : (
              <p className={`text-xs ${muted}`}>
               Drag and drop a session from the library into the calendar.
               Double-click a session to edit it.
              </p>
            )}
          </div>

          <div className={`flex items-center gap-1.5 px-2 py-1 rounded-lg border ${isDarkMode ? "border-gray-700 bg-gray-800/50" : "border-gray-200 bg-gray-50/50"}`} title="Zoom: Ctrl + Mouse wheel">
            <span className={`text-[10px] ${muted}`}>Zoom</span>
            <span className="text-xs font-semibold">{Math.round(zoom * 100)}%</span>
          </div>
          <div className={`text-xs ${muted}`}>
            Sessions : <span className="font-semibold">{(sessions || []).length}</span>
          </div>
        </div>
      </div>

      <div
        ref={wrapRef}
        className={`relative overflow-auto border-t ${isDarkMode ? "border-gray-700/40" : "border-gray-200"}`}
        onDragOver={onDragOver}
        onDrop={onDrop}
        style={{ height: WRAP_H }}
      >
        <div
          className="relative"
          style={{
            width: timeColW + daysCount * dayColW,
            height: headerH + heightPx,
          }}
        >
          
          {/* header row */}
          <div
            className="sticky top-0 z-20 flex"
            style={{ height: headerH }}
          >
            <div
              className={`flex items-center justify-center text-xs font-semibold ${muted} ${
                isDarkMode ? "bg-gray-950" : "bg-white"
              }`}
              style={{ width: timeColW, borderRight: `1px solid ${gridLine}` }}
            >
              Time
            </div>
            {dayColumns.map((d) => {
              const isMd = d.index === mdExactIndex;
              const isObjectiveCol = d.index === objectiveDayIndex;
              const isHover = hoverDay === d.index;
              const isCurrent = d.index === 0;
              const headerBgClass = isObjectiveCol
                ? (isDarkMode ? "bg-amber-400/10" : "bg-amber-500/10")
                : isHover
                  ? (isDarkMode ? "bg-emerald-400/10" : "bg-emerald-500/10")
                  : isCurrent
                    ? (isDarkMode ? "bg-sky-400/5" : "bg-sky-500/5")
                  : isMd
                    ? (isDarkMode ? "bg-emerald-400/5" : "bg-emerald-500/5")
                    : "";

              return (
              <div
                key={d.index}
                className={`flex flex-col items-center justify-center text-xs font-semibold leading-tight ${
                  isDarkMode ? "bg-gray-950" : "bg-white"
                } ${headerBgClass}`}
                style={{
                  width: dayColW,
                  borderRight: `1px solid ${gridLine}`,
                }}
              >
                {/* Ligne 1 : MD */}
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold">{d.md}</span>
                </div>

                {/* Ligne 2 : date */}
                {d.date ? (
                  <div className={`text-[10px] font-normal ${muted}`}>
                    {d.date}
                  </div>
                ) : null}
              </div>
            );
            })}
          </div>

          {/* time grid + day columns */}
          <div 
            className="absolute left-0 right-0" 
            style={{ top: headerH, height: heightPx }}
            onContextMenu={(e) => {
              // Only trigger if click is not on a session element
              if (e.target === e.currentTarget || e.target.closest('[data-grid-bg]')) {
                e.preventDefault();
                e.stopPropagation();
                const dayIndex = getDayIndexFromX(e.clientX);
                const startMin = getMinuteFromY(e.clientY);
                setEmptyContextMenu({
                  x: e.clientX,
                  y: e.clientY,
                  dayIndex,
                  start_min: startMin,
                });
                setContextMenu(null);
              }
            }}
          >
            {/* Current day column background (leftmost) */}
            {daysCount > 0 && objectiveDayIndex !== 0 ? (
              <div
                className={`absolute top-0 bottom-0 pointer-events-none ${
                  isDarkMode ? "bg-sky-400/5" : "bg-sky-500/5"
                }`}
                style={{
                  left: timeColW + 0 * dayColW,
                  width: dayColW,
                  zIndex: 0,
                }}
              />
            ) : null}

            {/* MD column background (subtle) */}
            {mdExactIndex >= 0 && objectiveDayIndex !== mdExactIndex ? (
              <div
                className={`absolute top-0 bottom-0 pointer-events-none ${
                  isDarkMode ? "bg-emerald-400/5" : "bg-emerald-500/5"
                }`}
                style={{
                  left: timeColW + mdExactIndex * dayColW,
                  width: dayColW,
                  zIndex: 0,
                }}
              />
            ) : null}

            {/* Objective (match) column background */}
            {hasObjective ? (
              <div
                className={`absolute top-0 bottom-0 pointer-events-none ${
                  isDarkMode ? "bg-amber-400/10" : "bg-amber-500/10"
                }`}
                style={{
                  left: timeColW + objectiveDayIndex * dayColW,
                  width: dayColW,
                  zIndex: 0,
                }}
              />
            ) : null}

            {/* time col */}
            <div
              className="absolute top-0 bottom-0"
              style={{ left: 0, width: timeColW, borderRight: `1px solid ${gridLine}` }}
            >
              {ticks.map((m) => {
                const y = (m - START_MIN) * pxPerMin;
                return (
                  <div
                    key={m}
                    className={`absolute left-0 right-0 text-[10px] ${muted}`}
                    style={{ top: y - 6 }}
                  >
                    <div className="px-2">{minutesToHHMM(m)}</div>
                  </div>
                );
              })}
              
              {/* Current time indicator */}
              {currentTimeY !== null && (
                <div
                  className="absolute left-0 right-0 flex items-center pointer-events-none"
                  style={{ top: currentTimeY, zIndex: 50 }}
                >
                  {/* Dot */}
                  <div 
                    className="w-2 h-2 rounded-full bg-red-500 ml-1 flex-shrink-0"
                    style={{ boxShadow: "0 0 4px rgba(239, 68, 68, 0.6)" }}
                  />
                  {/* Line */}
                  <div 
                    className="flex-1 h-[2px] bg-red-500"
                    style={{ boxShadow: "0 0 4px rgba(239, 68, 68, 0.4)" }}
                  />
                </div>
              )}
            </div>

            {/* horizontal lines */}
            {ticks.map((m) => {
              const y = (m - START_MIN) * pxPerMin;
              const thick = (m / 30) % 2 === 0;
              return (
                <div
                  key={`h-${m}`}
                  className="absolute left-0 right-0"
                  style={{
                    top: y,
                    height: 1,
                    background: gridLine,
                    opacity: thick ? 1 : 0.6,
                    zIndex: 1,
                  }}
                />
              );
            })}

            {/* day column separators */}
            {dayColumns.map((d) => {
              return (
                <div
                  key={`sep-${d.index}`}
                  className="absolute top-0 bottom-0"
                  style={{
                    left: timeColW + d.index * dayColW,
                    width: 1,
                    background: gridLine,
                    zIndex: 1,
                  }}
                />
              );
            })}

            {/* sessions */}
            {(sessions || []).map((s) => {
              const day = clamp(s.day_index ?? 0, 0, daysCount - 1);
              const y = (s.start_min - START_MIN) * pxPerMin;
              const h = Math.max(8, s.duration_min * pxPerMin);

              const label = String(s.label || "Session");
              const isMatch = s.event_type === "match" || /\bmatch\b/i.test(label);
              const isObjective = isMatch && day === objectiveDayIndex;
              const hasDescription = !!(s.description && s.description.trim());
              const isTraining = s.event_type === "training" || (!isMatch && s.event_type !== "rest");
              
              // Check if this is a player-specific session
              const isPlayerSpecific = s.is_player_specific === true || !!(s.player_id || s.player_ids?.length > 0);
              // Check if this general session has overlapping player-specific sessions
              const { laneIndexByUid, laneCountByUid, hasOverlappingPlayerSessions } = laneMaps[day] || {};
              const hasPlayerOverlap = hasOverlappingPlayerSessions?.get(s.uid) ?? false;

              const laneIndex = laneIndexByUid?.get(s.uid) ?? 0;
              const laneCount = laneCountByUid?.get(s.uid) ?? 1;

              const dayX = timeColW + day * dayColW;
              const colW = dayColW;
              const pad = 4;
              const laneW = (colW - pad * 2) / laneCount;
              const x = dayX + pad + laneIndex * laneW;
              const w = Math.max(24, laneW - 4);

              const active = drag?.uid === s.uid;
              
              // Get session style based on type
              const style = getSessionStyle(label);
              const IconComponent = style.icon;
              
              // Determine display mode based on card height
              const isUltraCompact = h < 32;  // Just icon
              const isCompact = h < 60;       // Icon + abbreviated label
              const isMedium = h < 100;       // Icon + label + time
              // else: full mode with inputs
              
              // Build player names display for merged sessions
              const playerCount = s.player_ids?.length || (s.player_id ? 1 : 0);
              const playerNamesDisplay = s.player_names?.length > 0 
                ? s.player_names.join(", ")
                : s.player_name || (s.player_id ? `Player (${s.player_id.slice(0, 8)}...)` : '');

              // Match-specific tooltip
              const matchTooltip = isMatch 
                ? `${label}\n${minutesToHHMM(s.start_min)} - ${s.duration_min}min\n\n🔒 Match locked\nEdit this match from the general calendar.`
                : isPlayerSpecific
                  ? `${label}\n${minutesToHHMM(s.start_min)} - ${s.duration_min}min\n👤 Player activity (${playerCount} player${playerCount > 1 ? 's' : ''}): ${playerNamesDisplay}\n⚠️ Not movable - follows general session${!hasDescription && isTraining ? '\n⚠️ No description' : ''}\nDouble-click to edit · Right-click for options`
                  : `${label}\n${minutesToHHMM(s.start_min)} - ${s.duration_min}min${hasPlayerOverlap ? '\n👥 General session with parallel player activities' : ''}${!hasDescription && isTraining ? '\n⚠️ No description' : ''}\nDouble-click to edit · Right-click for options`;
              
              // Determine if session is locked (match or player-specific)
              const isLocked = isMatch || isPlayerSpecific;

              return (
                <div
                  key={s.uid}
                  className={`absolute rounded-lg select-none transition-all overflow-hidden ${
                    isDarkMode ? style.bgDark : style.bgLight
                  } ${style.textClass} ${
                    active ? "ring-2 ring-white shadow-lg scale-[1.02]" : ""
                  } ${isMatch ? "ring-1 ring-amber-300/50" : ""} ${isPlayerSpecific ? "ring-1 ring-indigo-300/50" : ""} shadow-md hover:shadow-lg hover:brightness-110 ${isLocked ? "cursor-not-allowed" : "cursor-grab active:cursor-grabbing"}`}
                  style={{ top: y, left: x, width: w, height: h, zIndex: active ? 10 : 2 }}
                  onPointerDown={(e) => !isLocked && startMove(s.uid, e)}
                  onDoubleClick={() => {
                    if (isMatch) return; // Block match edit
                    // Always open session editor to modify blocks
                    onEditSession?.(s);
                  }}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (isMatch) return; // No context menu for matches
                    setContextMenu({ 
                      x: e.clientX, 
                      y: e.clientY, 
                      uid: s.uid, 
                      label,
                      description: s.description || "",
                      start_min: s.start_min,
                      duration_min: s.duration_min,
                      session_template_id: s.session_template_id,
                      event_type: s.event_type,
                      hasDescription,
                    });
                  }}
                  title={matchTooltip}
                >
                  {/* Accent stripe on left */}
                  <div 
                    className={`absolute left-0 top-0 bottom-0 w-1 ${style.accentClass}`}
                  />
                  
                  {/* Lock icon for matches */}
                  {isMatch && (
                    <div 
                      className="absolute top-1 right-1 p-1 rounded-full bg-black/30 z-10"
                      title="Match locked · Edit from the general calendar"
                    >
                      <Lock size={isUltraCompact ? 8 : 10} className="text-white/90" />
                    </div>
                  )}
                  
                  {/* Player-specific session indicator with lock */}
                  {isPlayerSpecific && !isMatch && (
                    <div 
                      className="absolute top-1 left-2 p-1 rounded-full bg-indigo-500/80 z-10 flex items-center gap-0.5"
                      title={`🔗 Specific activity (${playerCount} player${playerCount > 1 ? 's' : ''}): ${playerNamesDisplay}\nNot movable - follows general session`}
                    >
                      <Link size={isUltraCompact ? 6 : 8} className="text-white/80" />
                      {playerCount > 1 ? (
                        <>
                          <Users size={isUltraCompact ? 8 : 10} className="text-white" />
                          {!isUltraCompact && <span className="text-[8px] text-white font-bold">{playerCount}</span>}
                        </>
                      ) : (
                        <User size={isUltraCompact ? 8 : 10} className="text-white" />
                      )}
                    </div>
                  )}
                  
                  {/* General session indicator (when there are player sessions overlapping) */}
                  {!isPlayerSpecific && !isMatch && hasPlayerOverlap && (
                    <div 
                      className="absolute top-1 left-2 p-1 rounded-full bg-emerald-500/80 z-10"
                      title="General session (with parallel player activities)"
                    >
                      <Users size={isUltraCompact ? 8 : 10} className="text-white" />
                    </div>
                  )}
                  
                  {/* Missing description indicator for training sessions */}
                  {isTraining && !hasDescription && !isUltraCompact && (
                    <div 
                      className="absolute top-1 right-1 p-0.5 rounded-full bg-amber-500/80 z-10"
                      title="Missing description"
                    >
                      <AlertCircle size={isCompact ? 8 : 10} className="text-white" />
                    </div>
                  )}
                  
                  {/* Delete button - only for non-matches */}
                  {!isMatch && (
                    <button
                      className="absolute top-1 right-1 rounded-full p-0.5 bg-black/20 hover:bg-black/40 transition-colors opacity-0 hover:opacity-100 group-hover:opacity-100 z-10"
                      style={{ opacity: active ? 1 : undefined, right: isTraining && !hasDescription ? 20 : 4 }}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setDeleteConfirmModal({ uid: s.uid, label });
                      }}
                      title="Delete"
                    >
                      <X size={isUltraCompact ? 10 : 12} className="text-white/90" />
                    </button>
                  )}

                  {isUltraCompact ? (
                    /* Ultra compact: just centered icon */
                    <div className="flex items-center justify-center h-full">
                      <IconComponent size={14} className="text-white/90" />
                    </div>
                  ) : isCompact ? (
                    /* Compact: icon + truncated label */
                    <div className="flex items-center gap-1.5 p-1.5 pl-2.5 h-full">
                      <IconComponent size={14} className="text-white/90 flex-shrink-0" />
                      <span className="text-[10px] font-bold truncate leading-tight">
                        {label.length > 8 ? label.slice(0, 8) + "…" : label}
                      </span>
                    </div>
                  ) : isMedium ? (
                    /* Medium: icon + label + time info */
                    <div className="flex flex-col p-1.5 pl-2.5 h-full">
                      <div className="flex items-center gap-1.5">
                        <IconComponent size={14} className="text-white/90 flex-shrink-0" />
                        <span className="text-[11px] font-bold truncate leading-tight flex-1">
                          {label}
                        </span>
                      </div>
                      <div className="flex items-center gap-1 mt-1 text-white/80">
                        <Clock size={10} />
                        <span className="text-[9px] font-medium">
                          {minutesToHHMM(s.start_min)} · {s.duration_min}′
                        </span>
                      </div>
                      {/* Description indicator */}
                      {isTraining && !isMatch && (
                        <div className="flex items-center gap-1 mt-0.5 text-white/60">
                          <FileText size={8} />
                          <span className="text-[8px]">
                            {hasDescription ? "Description ✓" : "No description"}
                          </span>
                        </div>
                      )}
                    </div>
                  ) : (
                    /* Full: all info + editable inputs */
                    <div className="flex flex-col h-full">
                      <div className="flex items-start gap-2 p-2 pl-3">
                        <div className={`p-1.5 rounded-lg ${style.accentClass} flex-shrink-0`}>
                          <IconComponent size={16} className="text-white" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-bold truncate leading-tight">
                            {label}
                          </p>
                          <div className="flex items-center gap-1 mt-0.5 text-white/80">
                            <Clock size={10} />
                            <span className="text-[10px] font-medium">
                              {minutesToHHMM(s.start_min)} · {s.duration_min}min
                            </span>
                          </div>
                          {/* Description status */}
                          {isTraining && !isMatch && (
                            <div className={`flex items-center gap-1 mt-1 ${hasDescription ? "text-white/60" : "text-amber-300"}`}>
                              <FileText size={10} />
                              <span className="text-[9px]">
                                {hasDescription ? "Description ✓" : "⚠️ Missing description"}
                              </span>
                            </div>
                          )}
                          {/* Match locked info */}
                          {isMatch && (
                            <div className="flex items-center gap-1 mt-1 text-amber-200/80">
                              <Lock size={10} />
                              <span className="text-[9px]">Locked</span>
                            </div>
                          )}
                          {/* Player-specific or general session indicator */}
                          {!isMatch && isPlayerSpecific && (
                            <div className="flex items-center gap-1 mt-1 text-indigo-200/80">
                              {playerCount > 1 ? <Users size={10} /> : <User size={10} />}
                              <span className="text-[9px] truncate" title={playerNamesDisplay}>
                                {playerCount > 1 
                                  ? `${playerCount} players`
                                  : (s.player_name || 'Player activity')}
                              </span>
                            </div>
                          )}
                          {!isMatch && !isPlayerSpecific && hasPlayerOverlap && (
                            <div className="flex items-center gap-1 mt-1 text-emerald-200/80">
                              <Users size={10} />
                              <span className="text-[9px]">General session</span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Quick edit inputs - only show when there's enough space and NOT a match */}
                      {h >= 120 && !isMatch && (
                        <div className="px-2 pb-2 flex items-center gap-1.5 mt-auto">
                          <input
                            className="flex-1 min-w-0 rounded border px-1.5 py-0.5 text-[10px] outline-none bg-white/20 border-white/30 text-white placeholder-white/50 focus:bg-white/30"
                            defaultValue={minutesToHHMM(s.start_min)}
                            onBlur={(e) => inlineEditTime(s.uid, e.target.value, String(s.duration_min))}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") inlineEditTime(s.uid, e.currentTarget.value, String(s.duration_min));
                            }}
                            onClick={(e) => e.stopPropagation()}
                            onPointerDown={(e) => e.stopPropagation()}
                            title="Start time (HH:MM)"
                          />
                          <span className="text-[9px] text-white/70">{s.duration_min}′</span>
                        </div>
                      )}
                      
                      {/* Edit button for sessions without template */}
                      {h >= 120 && !isMatch && !s.session_template_id && (
                        <div className="px-2 pb-2">
                          <button
                            className="w-full flex items-center justify-center gap-1 rounded border px-2 py-1 text-[10px] bg-white/10 border-white/20 text-white/90 hover:bg-white/20 transition-colors"
                            onClick={(e) => {
                              e.stopPropagation();
                              openEditSessionModal(s);
                            }}
                            title="Edit title and description"
                          >
                            <Edit3 size={10} />
                            Edit
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Resize handle removed — session duration is driven by blocks */}
                </div>
              );
            })}
          </div>
        </div>

        {/* Undo/Redo floating buttons - sticky inside scroll container */}
        <div className="sticky bottom-3 left-0 right-0 flex justify-end pointer-events-none px-3 z-20" style={{ marginTop: -44 }}>
          <div className={`relative pointer-events-auto flex items-center gap-1 rounded-xl border px-2 py-1.5 ${
            isDarkMode ? "bg-gray-900/90 border-gray-700/80" : "bg-white/90 border-gray-200/80"
          } backdrop-blur-sm shadow-lg`}>
            {/* Info tooltip trigger */}
            <div className="group absolute -top-1 -right-1">
              <div className={`p-0.5 rounded-full cursor-help ${
                isDarkMode ? "bg-gray-700 text-gray-400 hover:text-gray-200" : "bg-gray-100 text-gray-400 hover:text-gray-600"
              }`}>
                <Info size={10} />
              </div>
              {/* Tooltip on hover */}
              <div className={`absolute bottom-full right-0 mb-2 w-52 p-2 rounded-lg border shadow-xl text-xs opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 ${
                isDarkMode ? "bg-gray-800 border-gray-700 text-gray-300" : "bg-white border-gray-200 text-gray-600"
              }`}>
                <p className="font-semibold mb-1.5">Shortcuts</p>
                <div className="space-y-1">
                  <p><kbd className={`px-1 rounded text-[10px] ${isDarkMode ? "bg-gray-700" : "bg-gray-100"}`}>Ctrl+S</kbd> Save</p>
                  <p><kbd className={`px-1 rounded text-[10px] ${isDarkMode ? "bg-gray-700" : "bg-gray-100"}`}>Ctrl+Z</kbd> Undo</p>
                  <p><kbd className={`px-1 rounded text-[10px] ${isDarkMode ? "bg-gray-700" : "bg-gray-100"}`}>Ctrl+Shift+Z</kbd> Redo</p>
                  <p className="pt-1 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}"><kbd className={`px-1 rounded text-[10px] ${isDarkMode ? "bg-gray-700" : "bg-gray-100"}`}>Ctrl+Wheel</kbd> Zoom</p>
                </div>
              </div>
            </div>

            {/* Save feedback message */}
            {saveMessage && (
              <span className={`text-xs font-medium mr-1 transition-opacity duration-300 ${
                saveMessage === "saving" ? "text-emerald-500 animate-pulse" : "text-emerald-500"
              }`}>
                {saveMessage === "saving" ? "Saving..." : "✓ Saved"}
              </span>
            )}
            
            {/* Save button */}
            <button
              className={`p-1.5 rounded-lg transition-colors text-emerald-500 hover:bg-emerald-500/20`}
              onClick={handleSave}
              title="Save microcycle (Ctrl+S)"
            >
              <Save size={16} />
            </button>

            {/* Import from calendar button */}
            {onImportCalendarEvents && (
              <div className="relative group">
                <button
                  className={`p-1.5 rounded-lg transition-colors relative ${
                    calendarEventsAvailable.length > 0
                      ? "text-blue-500 hover:bg-blue-500/20"
                      : (isDarkMode ? "text-gray-600 cursor-not-allowed" : "text-gray-300 cursor-not-allowed")
                  }`}
                  onClick={onImportCalendarEvents}
                  disabled={calendarEventsAvailable.length === 0}
                  title={calendarEventsAvailable.length > 0 
                    ? `Import ${calendarEventsAvailable.length} event(s) from calendar`
                    : "No events to import from calendar"
                  }
                >
                  <Calendar size={16} />
                  {/* Badge indicator */}
                  {calendarEventsAvailable.length > 0 && (
                    <span className="absolute -top-1 -right-1 min-w-[14px] h-[14px] flex items-center justify-center rounded-full bg-blue-500 text-white text-[9px] font-bold px-0.5">
                      {calendarEventsAvailable.length > 9 ? "9+" : calendarEventsAvailable.length}
                    </span>
                  )}
                </button>
                {/* Tooltip showing available events on hover */}
                {calendarEventsAvailable.length > 0 && (
                  <div className={`absolute bottom-full right-0 mb-2 w-64 p-2 rounded-lg border shadow-xl text-xs opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 ${
                    isDarkMode ? "bg-gray-800 border-gray-700 text-gray-300" : "bg-white border-gray-200 text-gray-600"
                  }`}>
                    <p className="font-semibold mb-1.5 flex items-center gap-1">
                      <Download size={12} />
                      Events to import ({calendarEventsAvailable.length})
                    </p>
                    <div className="max-h-40 overflow-y-auto space-y-1">
                      {calendarEventsAvailable.slice(0, 10).map((event, i) => (
                        <div key={i} className={`flex items-center gap-1.5 py-0.5 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${
                            event.period === "morning" ? "bg-yellow-400" : "bg-orange-400"
                          }`} />
                          <span className="truncate flex-1">{event.label || event.type || "Event"}</span>
                          <span className="text-[10px] opacity-60">{event.date?.slice(5)}</span>
                        </div>
                      ))}
                      {calendarEventsAvailable.length > 10 && (
                        <p className="text-[10px] opacity-60 pt-1">
                          ... and {calendarEventsAvailable.length - 10} more
                        </p>
                      )}
                    </div>
                    <p className={`mt-2 pt-1.5 border-t text-[10px] ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}>
                      Click to import these events
                    </p>
                  </div>
                )}
              </div>
            )}

            <div className={`w-px h-4 mx-0.5 ${isDarkMode ? "bg-gray-700" : "bg-gray-200"}`} />
            
            <button
              className={`p-1.5 rounded-lg transition-colors ${
                canUndo
                  ? (isDarkMode ? "hover:bg-gray-700 text-gray-200" : "hover:bg-gray-100 text-gray-700")
                  : (isDarkMode ? "text-gray-600 cursor-not-allowed" : "text-gray-300 cursor-not-allowed")
              }`}
              onClick={handleUndo}
              disabled={!canUndo}
              title="Undo (Ctrl+Z)"
            >
              <Undo2 size={16} />
            </button>
            <button
              className={`p-1.5 rounded-lg transition-colors ${
                canRedo
                  ? (isDarkMode ? "hover:bg-gray-700 text-gray-200" : "hover:bg-gray-100 text-gray-700")
                  : (isDarkMode ? "text-gray-600 cursor-not-allowed" : "text-gray-300 cursor-not-allowed")
              }`}
              onClick={handleRedo}
              disabled={!canRedo}
              title="Redo (Ctrl+Shift+Z)"
            >
              <Redo2 size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* hint when dragging from library (optional) */}
      {drag?.kind === "new" ? (
        <div className={`px-4 py-2 text-xs ${muted}`}>
          Drop the session on a day / time…
        </div>
      ) : null}

      {/* Context menu */}
      {contextMenu && (
        <div
          className={`fixed z-50 rounded-lg border shadow-xl py-1 min-w-[260px] max-w-[320px] ${
            isDarkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"
          }`}
          style={{
            top: Math.min(contextMenu.y, window.innerHeight - 400),
            left: Math.min(contextMenu.x, window.innerWidth - 280),
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className={`px-3 py-1.5 text-xs font-semibold truncate border-b flex items-center justify-between ${
            isDarkMode ? "border-gray-700 text-gray-400" : "border-gray-100 text-gray-500"
          }`}>
            <span>{contextMenu.label || "Session"}</span>
            {!contextMenu.hasDescription && contextMenu.event_type !== "match" && (
              <span className="flex items-center gap-1 text-amber-500">
                <AlertCircle size={10} />
                <span className="text-[10px]">No description</span>
              </span>
            )}
          </div>
          
          {/* Title and description edit section */}
          <div className={`px-3 py-2 border-b ${
            isDarkMode ? "border-gray-700" : "border-gray-100"
          }`}>
            <div className="flex items-center gap-2 mb-2">
              <label className={`text-xs w-16 ${
                isDarkMode ? "text-gray-400" : "text-gray-500"
              }`}>Title</label>
              <input
                type="text"
                className={`flex-1 rounded border px-2 py-1 text-xs outline-none ${
                  isDarkMode 
                    ? "bg-gray-700 border-gray-600 text-gray-200 focus:border-emerald-500" 
                    : "bg-white border-gray-200 text-gray-700 focus:border-emerald-500"
                }`}
                defaultValue={contextMenu.label}
                placeholder="Session title"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    updateSessionDetails(contextMenu.uid, { label: e.currentTarget.value });
                    setContextMenu((prev) => prev ? { ...prev, label: e.currentTarget.value } : null);
                  }
                }}
                onBlur={(e) => {
                  if (contextMenu) {
                    updateSessionDetails(contextMenu.uid, { label: e.target.value });
                    setContextMenu((prev) => prev ? { ...prev, label: e.target.value } : null);
                  }
                }}
              />
            </div>
            <div className="flex items-start gap-2">
              <label className={`text-xs w-16 pt-1 ${
                isDarkMode ? "text-gray-400" : "text-gray-500"
              }`}>Description</label>
              <textarea
                className={`flex-1 rounded border px-2 py-1 text-xs outline-none resize-none ${
                  isDarkMode 
                    ? "bg-gray-700 border-gray-600 text-gray-200 focus:border-emerald-500" 
                    : "bg-white border-gray-200 text-gray-700 focus:border-emerald-500"
                } ${!contextMenu.description?.trim() ? (isDarkMode ? "border-amber-500/50" : "border-amber-400") : ""}`}
                rows={2}
                defaultValue={contextMenu.description || ""}
                placeholder="Add a description..."
                onBlur={(e) => {
                  if (contextMenu) {
                    updateSessionDetails(contextMenu.uid, { description: e.target.value });
                    setContextMenu((prev) => prev ? { ...prev, description: e.target.value, hasDescription: !!e.target.value.trim() } : null);
                  }
                }}
              />
            </div>
          </div>
          
          {/* Time and duration edit fields */}
          <div className={`px-3 py-2 border-b ${
            isDarkMode ? "border-gray-700" : "border-gray-100"
          }`}>
            <div className="flex items-center gap-2 mb-2">
              <label className={`text-xs w-16 ${
                isDarkMode ? "text-gray-400" : "text-gray-500"
              }`}>Start</label>
              <input
                type="text"
                className={`flex-1 rounded border px-2 py-1 text-xs outline-none ${
                  isDarkMode 
                    ? "bg-gray-700 border-gray-600 text-gray-200 focus:border-emerald-500" 
                    : "bg-white border-gray-200 text-gray-700 focus:border-emerald-500"
                }`}
                defaultValue={minutesToHHMM(contextMenu.start_min)}
                placeholder="HH:MM"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    const value = e.currentTarget.value;
                    inlineEditTime(contextMenu.uid, value, String(contextMenu.duration_min));
                    setContextMenu((prev) => prev ? { ...prev, start_min: hhmmToMinutes(value) } : null);
                  }
                }}
                onBlur={(e) => {
                  const value = e.target.value;
                  if (contextMenu) {
                    inlineEditTime(contextMenu.uid, value, String(contextMenu.duration_min));
                    setContextMenu((prev) => prev ? { ...prev, start_min: hhmmToMinutes(value) } : null);
                  }
                }}
              />
            </div>
            <div className="flex items-center gap-2">
              <label className={`text-xs w-16 ${
                isDarkMode ? "text-gray-400" : "text-gray-500"
              }`}>Duration</label>
              <span className={`text-xs font-medium ${
                isDarkMode ? "text-gray-300" : "text-gray-700"
              }`}>{contextMenu.duration_min} min</span>
            </div>
          </div>
          
          {/* Always show edit session button */}
          <button
            className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors ${
              isDarkMode ? "hover:bg-gray-700 text-gray-200" : "hover:bg-gray-50 text-gray-700"
            }`}
            onClick={() => {
              // Find the session and open editor
              const session = (sessions || []).find((s) => s.uid === contextMenu.uid);
              if (session) {
                onEditSession?.(session);
              }
              setContextMenu(null);
            }}
          >
            <Edit3 size={14} className="text-emerald-500" />
            Edit session
          </button>
          <button
            className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors text-red-500 ${
              isDarkMode ? "hover:bg-red-500/10" : "hover:bg-red-50"
            }`}
            onClick={() => {
              setDeleteConfirmModal({ uid: contextMenu.uid, label: contextMenu.label });
              setContextMenu(null);
            }}
          >
            <Trash2 size={14} />
            Delete
          </button>
        </div>
      )}

      {/* Delete confirmation modal */}
      {deleteConfirmModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div 
            className="absolute inset-0 bg-black/60 backdrop-blur-sm" 
            onClick={() => setDeleteConfirmModal(null)} 
          />
          <div className={`relative w-full max-w-sm rounded-2xl border shadow-2xl ${
            isDarkMode ? "bg-gray-900 border-gray-700" : "bg-white border-gray-200"
          }`}>
            <div className={`px-5 py-4 border-b ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}>
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-xl ${isDarkMode ? "bg-red-500/20" : "bg-red-100"}`}>
                  <Trash2 size={20} className="text-red-500" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold">Delete session</h3>
                  <p className={`text-sm ${muted}`}>This action is irreversible</p>
                </div>
              </div>
            </div>
            
            <div className="p-5">
              <p className={`text-sm ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}>
                Are you sure you want to delete the session <strong>"{deleteConfirmModal.label}"</strong>?
              </p>
              <p className={`text-xs mt-2 ${muted}`}>
                The session will also be removed from the general calendar.
              </p>
            </div>
            
            <div className={`px-5 py-4 border-t flex justify-end gap-3 ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}>
              <button
                onClick={() => setDeleteConfirmModal(null)}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition ${
                  isDarkMode 
                    ? "bg-gray-800 hover:bg-gray-700 text-gray-300" 
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700"
                }`}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  removeSession(deleteConfirmModal.uid);
                  setDeleteConfirmModal(null);
                }}
                className="px-4 py-2 rounded-xl text-sm font-medium bg-red-600 hover:bg-red-700 text-white transition"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Empty space context menu */}
      {emptyContextMenu && (
        <div
          className={`fixed z-50 rounded-lg border shadow-xl py-1 min-w-[200px] ${
            isDarkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"
          }`}
          style={{
            top: Math.min(emptyContextMenu.y, window.innerHeight - 120),
            left: Math.min(emptyContextMenu.x, window.innerWidth - 220),
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className={`px-3 py-1.5 text-xs font-semibold border-b ${
            isDarkMode ? "border-gray-700 text-gray-400" : "border-gray-100 text-gray-500"
          }`}>
            {dayLabels?.[emptyContextMenu.dayIndex] || `Day ${emptyContextMenu.dayIndex + 1}`} · {minutesToHHMM(emptyContextMenu.start_min)}
          </div>
          
          <button
            className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors ${
              isDarkMode ? "hover:bg-gray-700 text-gray-200" : "hover:bg-gray-50 text-gray-700"
            }`}
            onClick={() => {
              onCreateNewSession?.({
                dayIndex: emptyContextMenu.dayIndex,
                startMin: emptyContextMenu.start_min,
                durationMin: 90,
              });
              setEmptyContextMenu(null);
            }}
          >
            <Plus size={14} className="text-emerald-500" />
            Create new session
          </button>
        </div>
      )}

      {/* Session edit modal (for sessions without template) */}
      {editSessionModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div 
            className="absolute inset-0 bg-black/60 backdrop-blur-sm" 
            onClick={() => setEditSessionModal(null)} 
          />
          <div className={`relative w-full max-w-md rounded-2xl border shadow-2xl ${
            isDarkMode ? "bg-gray-900 border-gray-700" : "bg-white border-gray-200"
          }`}>
            <div className={`px-5 py-4 border-b ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}>
              <h3 className="text-lg font-semibold">Edit session</h3>
              <p className={`text-sm ${muted}`}>Edit the title and description of this session</p>
            </div>
            
            <div className="p-5 space-y-4">
              <div>
                <label className={`block text-sm font-medium mb-1.5 ${muted}`}>
                  Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  className={`w-full rounded-xl border px-3 py-2 text-sm outline-none ${
                    isDarkMode 
                      ? "bg-gray-800 border-gray-700 text-gray-100 focus:border-emerald-500" 
                      : "bg-white border-gray-200 text-gray-900 focus:border-emerald-500"
                  }`}
                  value={editSessionModal.label}
                  onChange={(e) => setEditSessionModal((prev) => ({ ...prev, label: e.target.value }))}
                  placeholder="E.g.: Tactical training"
                />
              </div>
              
              <div>
                <label className={`block text-sm font-medium mb-1.5 ${muted}`}>
                  Description
                  {!editSessionModal.description?.trim() && (
                    <span className="ml-2 text-amber-500 text-xs font-normal">
                      <AlertCircle size={10} className="inline mr-0.5" />
                      Recommended
                    </span>
                  )}
                </label>
                <textarea
                  className={`w-full rounded-xl border px-3 py-2 text-sm outline-none resize-none ${
                    isDarkMode 
                      ? "bg-gray-800 border-gray-700 text-gray-100 focus:border-emerald-500" 
                      : "bg-white border-gray-200 text-gray-900 focus:border-emerald-500"
                  } ${!editSessionModal.description?.trim() ? (isDarkMode ? "border-amber-500/50" : "border-amber-400") : ""}`}
                  rows={4}
                  value={editSessionModal.description}
                  onChange={(e) => setEditSessionModal((prev) => ({ ...prev, description: e.target.value }))}
                  placeholder="Describe the content and objectives of this session..."
                />
                <p className={`text-xs mt-1 ${muted}`}>
                  The description helps the team understand the session objectives.
                </p>
              </div>
            </div>
            
            <div className={`px-5 py-4 border-t flex justify-end gap-3 ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}>
              <button
                onClick={() => setEditSessionModal(null)}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition ${
                  isDarkMode 
                    ? "bg-gray-800 hover:bg-gray-700 text-gray-300" 
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700"
                }`}
              >
                Cancel
              </button>
              <button
                onClick={saveEditSessionModal}
                disabled={!editSessionModal.label?.trim()}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition ${
                  editSessionModal.label?.trim()
                    ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                    : "bg-gray-400 text-gray-200 cursor-not-allowed"
                }`}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}